// App
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Components
import gmapInit from 'app/client/utils/map/gmapInit';
import CommuteAutocompleteItem from 'app/shared/modules/MobileSRP/Header/FilterModules/CommuteFilter/CommuteAutocompleteItem';

// Actions
import UserPointActions from 'app/shared/flux/actions/UserPointActions';

// Hooks
import { useAutocompleteDropdown } from './hooks';

// Lodash
import debounce from 'lodash/debounce';

// Icons
import IconSearchGrey from 'images/icons/search-v2-gray.min.svg';
import IconSearch from 'images/icons/search-v2.min.svg';
import IconGoogleAttribution from 'images/googleAttribution.png';

// Tracking

// Misc / utils
import VALUE_CONSTANTS from 'app/shared/constants/ValueConstants';
import * as S from './styles';

interface UserPoint {
  id: string;
  lat: number;
  lon: number;
  name: string;
  address: string;
  active?: boolean;
}

interface AddressCoordinates {
  lat: number;
  lon: number;
}

interface Suggestion {
  description: string;
  primaryText: string;
  secondaryText: string;
  placeId: string;
  ref: React.RefObject<HTMLLIElement>;
}

interface CommuteAutocompleteProps {
  setIsAddressLoaded: (loaded: boolean) => void;
  handleAddressChange: (coords: AddressCoordinates) => void;
  userPoint: UserPoint | null;
  showDropdown: boolean;
  setShowDropdown: (show: boolean) => void;
  inputValue: string;
  onInputChange: (value: string) => void;
}

interface RootState {
  app: {
    googleMaps: {
      key: string;
    };
  };
  user: {
    userPoints: {
      destinations: Array<UserPoint>;
    };
  };
}

const CommuteAutocomplete: React.FC<CommuteAutocompleteProps> = ({
  setIsAddressLoaded,
  handleAddressChange,
  userPoint,
  showDropdown,
  setShowDropdown,
  inputValue,
  onInputChange,
}) => {
  // Redux
  const dispatch = useDispatch();
  const googleMapsKey = useSelector((state: RootState) => state.app.googleMaps.key);
  const userPoints = useSelector((state: RootState) => state.user.userPoints.destinations);

  // Deduplicate user points based on address/name to prevent duplicate entries in the dropdown
  const uniqueUserPoints = useMemo(() => {
    const seen = new Set<string>();
    return userPoints.filter((point) => {
      const normalizedName = point.name.toLowerCase().trim();
      if (seen.has(normalizedName)) {
        return false;
      }
      seen.add(normalizedName);
      return true;
    });
  }, [userPoints]);

  // React
  const autocompleteInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
  const geocoderService = useRef<google.maps.Geocoder | null>(null);
  const [autocompleteRefList, setAutocompleteRefList] = useState<Array<React.RefObject<HTMLLIElement>>>([]);
  const [suggestions, setSuggestions] = useState<Array<Suggestion> | null>(null);

  // Hooks
  const [autocompleteBasicKeyDownNav, autocompleteKeyDownNav] = useAutocompleteDropdown({
    focusRef: autocompleteInputRef,
    refList: autocompleteRefList,
    showDropdown,
    toggleDropdown: setShowDropdown,
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showDropdown && containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown, setShowDropdown]);

  useEffect(() => {
    if (!autocompleteService.current || !geocoderService.current) {
      void gmapInit({ key: googleMapsKey })
        .then(() => {
          autocompleteService.current = new window.google.maps.places.AutocompleteService();
          geocoderService.current = new window.google.maps.Geocoder();
          return;
        })
        .catch((error: unknown) => {
          // eslint-disable-next-line no-console
          console.error('Failed to initialize Google Maps:', error);
        });
    }
  }, [googleMapsKey]);

  useEffect(() => {
    if (autocompleteRefList.length < 1 && uniqueUserPoints.length > 0) {
      const refArr: Array<React.RefObject<HTMLLIElement>> = [];
      uniqueUserPoints.forEach(() => {
        const ref = React.createRef<HTMLLIElement>();
        refArr.push(ref);
      });

      setAutocompleteRefList(refArr);
    }
  }, [autocompleteRefList.length, uniqueUserPoints]);

  const debouncedFetch = useRef(
    debounce(
      (query: string) => {
        if (!autocompleteService.current) return;

        autocompleteService.current.getPlacePredictions(
          {
            input: query,
            componentRestrictions: { country: 'us' },
          },
          (predictions: Array<google.maps.places.AutocompletePrediction> | null) => {
            if (!predictions) return;

            const refArr: Array<React.RefObject<HTMLLIElement>> = [];
            const newSuggestions: Array<Suggestion> = predictions.map((place) => {
              const ref = React.createRef<HTMLLIElement>();
              refArr.push(ref);
              return {
                description: place.description,
                primaryText: place.structured_formatting.main_text,
                secondaryText: place.structured_formatting.secondary_text,
                placeId: place.place_id,
                ref,
              };
            });
            setAutocompleteRefList(refArr);
            setSuggestions(newSuggestions);
          },
        );
      },
      600,
      { leading: true, trailing: true },
    ),
  ).current;

  // Input field handlers
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!showDropdown) {
      setShowDropdown(true);
    }

    onInputChange(e.target.value);
    debouncedFetch(e.target.value);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => autocompleteBasicKeyDownNav(e.keyCode);

  // Item or user point selection handlers
  const handleSuggestionSelection = (suggestion: Suggestion) => {
    setIsAddressLoaded(false);
    if (!geocoderService.current) return;

    geocoderService.current.geocode(
      { placeId: suggestion.placeId },
      (results: Array<google.maps.GeocoderResult> | null) => {
        if (!results || results.length === 0) return;

        const location = results[0].geometry.location;
        const newUserPointObj = {
          lat: location.lat(),
          lon: location.lng(),
          name: suggestion.description,
        };

        void Promise.resolve(dispatch(UserPointActions.create(newUserPointObj, { active: true })))
          .then(() => {
            setIsAddressLoaded(true);
            return;
          })
          .catch((error: unknown) => {
            // eslint-disable-next-line no-console
            console.error('Failed to create user point:', error);
          });
        handleAddressChange({ lat: newUserPointObj.lat, lon: newUserPointObj.lon });
        setShowDropdown(false);
      },
    );
  };

  const handlePreviousPointSelection = (point: UserPoint) => {
    dispatch(UserPointActions.select(point.address));
    handleAddressChange({ lat: point.lat, lon: point.lon });
    setShowDropdown(false);
  };

  // Click handlers
  const handleSuggestionItemClick = (item: { primaryText: string; secondaryText: string }) =>
    handleSuggestionSelection(item as Suggestion);
  const handlePreviousPointClick = (item: { primaryText: string; secondaryText: string }) =>
    handlePreviousPointSelection(item as unknown as UserPoint);

  // Keydown handlers
  const handleSuggestionItemKeyDown = (
    e: React.KeyboardEvent,
    item: { primaryText: string; secondaryText: string },
  ) => {
    e.stopPropagation();

    const keyCode = e.keyCode;
    const enterKeyPressed = keyCode === VALUE_CONSTANTS.ENTER_KEYCODE;

    if (enterKeyPressed) {
      handleSuggestionSelection(item as Suggestion);
    } else {
      autocompleteKeyDownNav(keyCode);
    }
  };

  const handlePreviousPointKeyDown = (e: React.KeyboardEvent, item: { primaryText: string; secondaryText: string }) => {
    e.stopPropagation();

    const keyCode = e.keyCode;
    const enterKeyPressed = keyCode === VALUE_CONSTANTS.ENTER_KEYCODE;

    if (enterKeyPressed) {
      handlePreviousPointSelection(item as unknown as UserPoint);
    } else {
      autocompleteKeyDownNav(keyCode);
    }
  };

  const shouldShowSuggestions = suggestions !== null && suggestions.length > 0;
  const shouldShowPreviousUserPoints = suggestions === null && uniqueUserPoints.length > 0;
  let numberOfItems;

  if (shouldShowSuggestions) {
    numberOfItems = suggestions.length;
  }
  if (shouldShowPreviousUserPoints) {
    numberOfItems = uniqueUserPoints.length;
  }

  return (
    <div ref={containerRef}>
      <S.AutocompleteBarContainer>
        <S.InputIcon
          src={showDropdown ? IconSearch : IconSearchGrey}
          height="16px"
          width="16px"
          aria-hidden="true"
          loading="lazy"
        />
        <S.FocusLock disabled={!showDropdown}>
          <S.Input
            aria-autocomplete="list"
            aria-expanded={showDropdown}
            autoCapitalize="none"
            autoComplete="off"
            id="commute search box"
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            placeholder={shouldShowPreviousUserPoints && userPoint ? userPoint.address : 'Where is your commute?'}
            role="combobox"
            ref={autocompleteInputRef}
            type="text"
            value={inputValue}
          />
          {showDropdown && (shouldShowSuggestions || shouldShowPreviousUserPoints) && (
            <>
              <S.ScreenReaderOnly role="status" aria-live="polite">
                {numberOfItems} {numberOfItems === 1 ? 'result' : 'results'} available. Use the up and down arrows to
                browse and enter to select and go to item. Use the escape key to exit.
              </S.ScreenReaderOnly>
              <S.MenuBox role="listbox" id="listbox">
                <S.ScrollableList>
                  <ul role="group" aria-labelledby="suggestion-category">
                    {shouldShowSuggestions && (
                      <>
                        <S.SuggestionCategory role="presentation" id="suggestion-category">
                          Suggestions
                        </S.SuggestionCategory>
                        {suggestions.map((suggestion) => {
                          const suggestionRef = suggestion.ref;
                          return (
                            <CommuteAutocompleteItem
                              item={suggestion}
                              handleSelectionClick={handleSuggestionItemClick}
                              handleSelectionKeyDown={handleSuggestionItemKeyDown}
                              customRef={suggestionRef}
                              key={suggestion.placeId}
                            />
                          );
                        })}
                      </>
                    )}
                    {shouldShowPreviousUserPoints && (
                      <>
                        <S.SuggestionCategory role="presentation" id="suggestion-category">
                          Previous locations
                        </S.SuggestionCategory>
                        {uniqueUserPoints.map((point, i) => {
                          const pointId = point.id;
                          const pointDetails = point.name.split(',');
                          const formattedItem = {
                            ...point,
                            primaryText: pointDetails[0],
                            secondaryText: pointDetails.slice(1, pointDetails.length).join(','),
                          };
                          const pointRef = autocompleteRefList[i];

                          return (
                            <CommuteAutocompleteItem
                              item={formattedItem}
                              handleSelectionClick={handlePreviousPointClick}
                              handleSelectionKeyDown={handlePreviousPointKeyDown}
                              customRef={pointRef}
                              key={pointId}
                            />
                          );
                        })}
                      </>
                    )}
                  </ul>
                </S.ScrollableList>
                <S.GoogleAttribution src={IconGoogleAttribution} alt="Powered by Google" loading="lazy" />
              </S.MenuBox>
            </>
          )}
        </S.FocusLock>
      </S.AutocompleteBarContainer>
    </div>
  );
};

export default CommuteAutocomplete;
